/**
 * User Profile Page
 * Shows current user info: role, creation date, assigned sites, etc.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { User, Shield, Mail, Building2, MapPin, Calendar, Clock, Lock } from 'lucide-react';

const roleLabels: Record<string, string> = {
  admin: 'Administrator',
  'site-rc': 'Site Research Coordinator',
  site: 'Site User',
  analyst: 'Core Lab Analyst',
  cec: 'CEC Member',
};

const roleColors: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  'site-rc': 'bg-blue-100 text-blue-800',
  site: 'bg-opci-navy-100 text-opci-navy-700',
  analyst: 'bg-green-100 text-green-800',
  cec: 'bg-amber-100 text-amber-800',
};

const Profile: React.FC = () => {
  const { user, studyName } = useAuth();

  if (!user) return null;

  const infoItems = [
    { icon: User, label: 'Username', value: user.username },
    { icon: Mail, label: 'Email', value: user.email },
    {
      icon: Shield,
      label: 'Role',
      value: (
        <span className={`px-3 py-1 text-sm rounded-full font-medium ${roleColors[user.role] || ''}`}>
          {roleLabels[user.role] || user.role}
        </span>
      ),
    },
    { icon: Building2, label: 'Organization', value: user.organization || '—' },
    {
      icon: MapPin,
      label: 'Assigned Sites',
      value:
        user.assignedSites && user.assignedSites.length > 0
          ? user.assignedSites.map(s => `${s.siteId} - ${s.siteName}`).join(', ')
          : user.role === 'admin'
          ? 'All sites (admin)'
          : '—',
    },
    {
      icon: Calendar,
      label: 'Account Created',
      value: user.createdAt
        ? new Date(user.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })
        : '—',
    },
    {
      icon: Clock,
      label: 'Last Login',
      value: user.lastLogin
        ? new Date(user.lastLogin).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
        : 'First session',
    },
  ];

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl shadow-sm p-8">
        <div className="flex items-center gap-5">
          <div className="w-16 h-16 bg-opci-navy-100 rounded-full flex items-center justify-center">
            <User className="w-8 h-8 text-opci-navy-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{user.fullName}</h1>
            <p className="text-gray-500">{studyName || 'OPCI EDC'}</p>
          </div>
        </div>
      </div>

      {/* Info Card */}
      <div className="bg-white rounded-xl shadow-sm divide-y">
        {infoItems.map((item, idx) => (
          <div key={idx} className="flex items-center px-6 py-4 gap-4">
            <item.icon className="w-5 h-5 text-gray-400 flex-shrink-0" />
            <span className="text-sm font-medium text-gray-500 w-36">{item.label}</span>
            <span className="text-sm text-gray-900 flex-1">
              {typeof item.value === 'string' ? item.value : item.value}
            </span>
          </div>
        ))}
      </div>

      {/* Actions */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <Link
          to="/change-password"
          className="inline-flex items-center gap-2 px-4 py-2 bg-opci-navy-500 text-white rounded-lg hover:bg-opci-navy-600 transition-colors"
        >
          <Lock className="w-4 h-4" />
          Change Password
        </Link>
      </div>
    </div>
  );
};

export default Profile;
